import React, { useEffect } from 'react';
import { useYouTubePlayer } from '../../hooks/use-youtube-player';

interface YouTubePlayerProps {
  videoId: string;
  autoplay?: boolean;
  controls?: boolean;
  muted?: boolean;
  width?: string | number;
  height?: string | number;
  startSeconds?: number;
  endSeconds?: number;
  onReady?: (player: any) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  onError?: (event: any) => void;
  onTimeUpdate?: (currentTime: number) => void;
  className?: string;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  autoplay = false,
  controls = true,
  muted = false,
  width = '100%',
  height = '100%',
  startSeconds = 0,
  endSeconds,
  onReady,
  onPlay,
  onPause,
  onEnd,
  onError,
  onTimeUpdate,
  className = ''
}) => {
  const {
    playerRef,
    isReady,
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    seekTo,
    setVolume,
    loadVideo
  } = useYouTubePlayer({
    videoId,
    autoplay,
    controls,
    width,
    height,
    startSeconds,
    onReady: (player) => {
      if (muted) player.mute();
      onReady?.(player);
    },
    onStateChange: (event) => {
      // PlayerState: UNSTARTED=-1, ENDED=0, PLAYING=1, PAUSED=2, BUFFERING=3, CUED=5
      if (event.data === 1) {
        onPlay?.();
      } else if (event.data === 2) {
        onPause?.();
      } else if (event.data === 0) {
        onEnd?.();
      }
    },
    onError: (event) => onError?.(event)
  });

  // Call onTimeUpdate when currentTime changes
  useEffect(() => {
    if (isPlaying && onTimeUpdate) {
      onTimeUpdate(currentTime);
    }
  }, [currentTime, isPlaying, onTimeUpdate]);

  // Auto-pause when endSeconds is reached
  useEffect(() => {
    if (endSeconds !== undefined && isPlaying && currentTime >= endSeconds) {
      pause();
    }
  }, [currentTime, isPlaying, endSeconds, pause]);

  return (
    <div
      className={`youtube-player-wrapper ${className}`}
      style={
        {
          '--player-width': typeof width === 'number' ? `${width}px` : width,
          '--player-height': typeof height === 'number' ? `${height}px` : height
        } as React.CSSProperties
      }
    >
      <div
        ref={playerRef}
        className='youtube-player-container'
      />
      {!isReady && (
        <div className='youtube-player-loading'>Loading player...</div>
      )}
    </div>
  );
};

export default YouTubePlayer;
